import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Output, inject } from '@angular/core';
import { FormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import {
  Organization,
  OrganizationStatus,
  OrganizationType,
} from '@nimmsta/ng-commons-lib/models';
import { OrganizationService } from '@nimmsta/ng-commons-lib/services/organization';
import {
  SaveableFormComponent,
  InputWithErrorComponent,
} from '@nimmsta/ng-commons-lib/forms';
import { ErrorComponent } from '@nimmsta/ng-commons-lib/error';
import { TranslatePipe } from '@ngx-translate/core';
import { Select } from 'primeng/select';

@Component({
  selector: 'app-organization-form',
  templateUrl: 'organization-form.component.html',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    InputWithErrorComponent,
    ErrorComponent,
    Select,
  ],
})
export class OrganizationFormComponent extends SaveableFormComponent<Organization> {
  private fb = inject(FormBuilder);

  protected override dataService = inject(OrganizationService);

  @Output() override closeEvent = new EventEmitter();

  @Output() override loadingEvent = new EventEmitter<boolean>();

  status = Object.values(OrganizationStatus);

  types = Object.values(OrganizationType);

  typeNames = this.types.map((type) => `organizations.typeItems.${type}`);

  partnerOrganizations: Organization[] = [];

  motherOrganizations: Organization[] = [];

  organizationsLoading = true;

  protected readonly OrganizationType = OrganizationType;

  constructor() {
    super();
    this.setValue(new Organization());
  }

  protected setFormGroup(): void {
    this.form = this.fb.group({
      id: this.value.id,
      name: [this.value.name, [Validators.required]],
      address: [this.value.address, []],
      description: [this.value.description, []],
      status: [this.value.status, [Validators.required]],
      type: [this.value.type, [Validators.required]],
      parentOrganizationId: [
        this.value.parentOrganizationId ?? this.value.parentOrganization?.id,
      ],
      motherOrganizationId: [
        this.value.motherOrganizationId ?? this.value.motherOrganization?.id,
      ],
    });
  }

  override applyCustomValues() {
    super.applyCustomValues();
    const canHaveParent = this.value.type === OrganizationType.CUSTOMER;
    this.value = {
      ...this.value,
      parentOrganization: undefined,
      parentOrganizationId: canHaveParent
        ? this.value.parentOrganizationId
        : undefined,
      motherOrganization: undefined,
      motherOrganizationId: this.value.motherOrganizationId,
    } as Organization;
  }

  loadOrganizations(): void {
    this.organizationsLoading = true;
    this.dataService.list({ pageSize: 9999 }).subscribe({
      next: (response) => {
        setTimeout(() => {
          const organizations = response.content.filter(
            (organization) => organization.id !== this.value.id,
          );
          this.motherOrganizations = organizations;
          this.partnerOrganizations = organizations.filter(
            (organization) => organization.type === OrganizationType.PARTNER,
          );
          this.organizationsLoading = false;
        }, 0);
      },
      error: (error: HttpErrorResponse) => {
        this.error = error;
        this.organizationsLoading = false;
      },
    });
  }
}
